import React, { useState } from 'react';

import { motion } from 'framer-motion';
import { BarChart2, BookOpen, ExternalLink, Info, TrendingUp } from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { digitalImpactSources, metricToSourceMap } from '../data/digitalImpactSources';

interface DigitalImpactChartProps {
  className?: string;
}

const DigitalImpactChart: React.FC<DigitalImpactChartProps> = ({ className = '' }) => {
  // Estados para controlar qué métricas se muestran en detalle
  const [activeDigitalMetric, setActiveDigitalMetric] = useState<string | null>(null);
  const [showSources, setShowSources] = useState<boolean>(false);

  // Datos para el gráfico de barras - Impacto de presencia digital
  const digitalImpactData = [
    { name: 'Ventas', conPresencia: 40, sinPresencia: 10, diferencia: '+30%' },
    { name: 'Clientes', conPresencia: 45, sinPresencia: 12, diferencia: '+33%' },
    { name: 'Visibilidad', conPresencia: 65, sinPresencia: 15, diferencia: '+50%' },
    { name: 'Retención', conPresencia: 60, sinPresencia: 25, diferencia: '+35%' },
    { name: 'Conversión', conPresencia: 35, sinPresencia: 8, diferencia: '+27%' },
  ];

  // Función para mostrar información detallada al hacer clic en una métrica
  const handleDigitalMetricClick = (metricName: string) => {
    setActiveDigitalMetric(activeDigitalMetric === metricName ? null : metricName);
  };

  // Información detallada para cada métrica de impacto digital
  const digitalImpactDetails = {
    Ventas:
      'Las empresas con presencia digital profesional experimentan un aumento del 40% en ventas frente al 10% en negocios sin presencia online. Esto representa una diferencia de 30 puntos porcentuales, equivalente a $300,000 adicionales por cada millón en ventas. Este incremento se debe a la expansión del mercado potencial, disponibilidad 24/7 y mayor alcance geográfico.',
    Clientes:
      'La captación de nuevos clientes aumenta un 45% con estrategias digitales efectivas, comparado con solo un 12% en negocios tradicionales. Esta diferencia del 33% se traduce en aproximadamente 330 clientes adicionales por cada 1,000 prospectos, multiplicando el potencial de crecimiento del negocio.',
    Visibilidad:
      'La visibilidad de marca mejora un 65% con presencia digital optimizada, versus un 15% para negocios sin estrategia digital. Esta diferencia del 50% significa que su marca será reconocida por 5 de cada 10 personas adicionales en su mercado objetivo, creando una ventaja competitiva sustancial.',
    Retención:
      'La tasa de retención de clientes alcanza un 60% con herramientas digitales que facilitan la comunicación y seguimiento, versus un 25% en negocios tradicionales. Esta diferencia del 35% representa un valor de cliente a largo plazo 2.4 veces mayor, impactando directamente en la rentabilidad.',
    Conversión:
      'La tasa de conversión de prospectos a clientes es del 35% con estrategias digitales efectivas, mientras que sin presencia digital apenas alcanza un 8%. Esta diferencia del 27% significa que por cada 100 visitantes, su negocio obtiene 27 clientes más que la competencia sin presencia digital.',
  };

  return (
    <section className={`py-16 ${className}`}>
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Impacto de Presencia Digital</h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-4">
            Descubra cómo una presencia digital profesional puede transformar el rendimiento de su
            negocio con datos comparativos basados en estudios de mercado y casos reales.
          </p>
          <p className="text-md text-gray-700 max-w-2xl mx-auto italic border-l-4 border-blue-400 pl-4 py-2 bg-blue-50">
            Nuestras soluciones digitales están diseñadas para maximizar su impacto en el mercado,
            generando resultados medibles y sostenibles que superan significativamente a los métodos
            tradicionales.
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-6 mb-12">
          {/* Impacto de Presencia Digital - Gráfico comparativo */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-gradient-to-br from-gray-50 to-blue-50 p-6 rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 border border-gray-100"
          >
            <div className="flex justify-between items-center mb-6">
              <h4 className="text-xl font-semibold text-gray-800 flex items-center">
                <BarChart2 className="h-5 w-5 mr-2 text-blue-600" />
                Impacto de Presencia Digital (%)
              </h4>
              <div className="flex space-x-2">
                <button
                  onClick={() => setShowSources(!showSources)}
                  className="px-3 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full flex items-center hover:bg-blue-200 transition-colors"
                >
                  <BookOpen className="h-3 w-3 mr-1" />
                  {showSources ? 'Ocultar fuentes' : 'Ver fuentes de datos'}
                </button>
                <span className="px-3 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full flex items-center">
                  <BarChart2 className="h-3 w-3 mr-1" /> Datos validados 2023
                </span>
              </div>
            </div>

            {/* Panel de fuentes de datos */}
            {showSources && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
                className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-100"
              >
                <div className="flex items-center justify-between mb-3">
                  <h5 className="text-sm font-semibold text-blue-800 flex items-center">
                    <Info className="h-4 w-4 mr-1" /> Fuentes de datos utilizadas
                  </h5>
                  <button
                    onClick={() => setShowSources(false)}
                    className="text-blue-500 hover:text-blue-700 text-xs"
                  >
                    Cerrar
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {digitalImpactSources.map((source, index) => (
                    <div
                      key={index}
                      className="bg-white p-3 rounded-md shadow-sm border border-blue-100"
                    >
                      <div className="flex justify-between items-start">
                        <h6 className="text-sm font-medium text-gray-800">{source.name}</h6>
                        <span className="bg-blue-100 text-blue-800 text-xs px-2 py-0.5 rounded">
                          {source.year}
                        </span>
                      </div>
                      <p className="text-xs text-gray-600 mt-1 mb-2">{source.description}</p>
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-gray-500">{source.organization}</span>
                        <a
                          href={source.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-blue-600 hover:text-blue-800 flex items-center"
                        >
                          Verificar fuente <ExternalLink className="h-3 w-3 ml-1" />
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}

            <div className="h-96">
              {' '}
              {/* Altura aumentada para mejor visualización */}
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={digitalImpactData}
                  margin={{ top: 10, right: 30, left: 20, bottom: 10 }}
                  barGap={8}
                  barSize={32}
                >
                  <defs>
                    <linearGradient id="colorConPresencia" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.9} />
                      <stop offset="95%" stopColor="#60A5FA" stopOpacity={0.8} />
                    </linearGradient>
                    <linearGradient id="colorSinPresencia" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#9CA3AF" stopOpacity={0.8} />
                      <stop offset="95%" stopColor="#D1D5DB" stopOpacity={0.7} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis
                    dataKey="name"
                    tick={{ fill: '#4B5563', fontWeight: 500 }}
                    axisLine={{ stroke: '#E5E7EB' }}
                  />
                  <YAxis
                    domain={[0, 80]}
                    tick={{ fill: '#4B5563' }}
                    axisLine={{ stroke: '#E5E7EB' }}
                    tickFormatter={value => `${value}%`}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'rgba(255, 255, 255, 0.95)',
                      borderRadius: '8px',
                      border: '1px solid #E5E7EB',
                      boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                    }}
                    formatter={(value, name, props) => {
                      const item = digitalImpactData.find(d => d.name === props.payload.name);
                      if (name === 'conPresencia') {
                        return [
                          <div>
                            <div className="font-medium">{value}%</div>
                            {item && (
                              <div className="text-xs text-green-600 mt-1">
                                Diferencia: {item.diferencia}
                              </div>
                            )}
                          </div>,
                          'Con Presencia Digital',
                        ];
                      }
                      if (name === 'sinPresencia') {
                        return [`${value}%`, 'Sin Presencia Digital'];
                      }
                      return [value, name];
                    }}
                    cursor={{ fill: 'rgba(0, 0, 0, 0.05)' }}
                    labelFormatter={name => (
                      <span className="font-medium text-gray-800">Métrica: {name}</span>
                    )}
                  />
                  <Legend
                    verticalAlign="top"
                    height={36}
                    wrapperStyle={{ paddingTop: '10px' }}
                    formatter={value => <span className="text-sm font-medium">{value}</span>}
                  />
                  <Bar
                    dataKey="conPresencia"
                    name="Con Presencia Digital"
                    fill="url(#colorConPresencia)"
                    animationDuration={1800}
                    animationEasing="ease-in-out"
                    onClick={data => handleDigitalMetricClick(data.name)}
                    radius={[4, 4, 0, 0]}
                    activeBar={{ stroke: '#2563EB', strokeWidth: 2 }}
                  />
                  <Bar
                    dataKey="sinPresencia"
                    name="Sin Presencia Digital"
                    fill="url(#colorSinPresencia)"
                    animationDuration={1800}
                    animationEasing="ease-in-out"
                    onClick={data => handleDigitalMetricClick(data.name)}
                    radius={[4, 4, 0, 0]}
                    activeBar={{ stroke: '#6B7280', strokeWidth: 2 }}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="mt-3 text-sm text-gray-600 text-center bg-blue-50 p-2 rounded-lg border border-blue-100">
              <p className="flex items-center justify-center">
                <Info className="h-4 w-4 text-blue-500 mr-1" />
                Haga clic en las barras para ver análisis detallado de cada métrica
              </p>
            </div>

            {/* Panel de información detallada para impacto digital - Mejorado */}
            {activeDigitalMetric && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
                exit={{ opacity: 0, height: 0 }}
                className="mt-5 p-5 bg-white rounded-lg border border-gray-200 shadow-sm"
              >
                <div className="flex items-start">
                  <Info className="h-6 w-6 text-blue-500 mr-3 mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <h5 className="text-lg font-semibold text-gray-800">{activeDigitalMetric}</h5>
                      <span className="px-3 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full flex items-center">
                        <TrendingUp className="h-3 w-3 mr-1" />
                        Diferencia:{' '}
                        {
                          digitalImpactData.find(item => item.name === activeDigitalMetric)
                            ?.diferencia
                        }
                      </span>
                    </div>

                    <p className="text-gray-600 mb-3">
                      {
                        digitalImpactDetails[
                          activeDigitalMetric as keyof typeof digitalImpactDetails
                        ]
                      }
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4 bg-gray-50 p-3 rounded-lg">
                      <div className="bg-blue-50 p-3 rounded-lg">
                        <p className="text-xs text-blue-700 font-medium">Con presencia digital:</p>
                        <p className="text-xl font-bold text-blue-800">
                          {
                            digitalImpactData.find(item => item.name === activeDigitalMetric)
                              ?.conPresencia
                          }
                          %
                        </p>
                      </div>
                      <div className="bg-gray-100 p-3 rounded-lg">
                        <p className="text-xs text-gray-700 font-medium">Sin presencia digital:</p>
                        <p className="text-xl font-bold text-gray-800">
                          {
                            digitalImpactData.find(item => item.name === activeDigitalMetric)
                              ?.sinPresencia
                          }
                          %
                        </p>
                      </div>
                      <div className="bg-green-50 p-3 rounded-lg">
                        <p className="text-xs text-green-700 font-medium">Impacto en su negocio:</p>
                        <p className="text-xl font-bold text-green-800">
                          {
                            digitalImpactData.find(item => item.name === activeDigitalMetric)
                              ?.diferencia
                          }
                        </p>
                      </div>
                    </div>

                    <div className="mt-3 text-xs text-gray-500">
                      <p className="italic mb-1">
                        * Datos basados en estudios de mercado y análisis de casos reales en
                        empresas similares.
                      </p>

                      {/* Fuentes específicas para esta métrica */}
                      {activeDigitalMetric && metricToSourceMap[activeDigitalMetric] && (
                        <div className="mt-2 pt-2 border-t border-gray-200">
                          <p className="font-medium mb-1 flex items-center">
                            <BookOpen className="h-3 w-3 mr-1 text-blue-500" />
                            Fuentes para esta métrica:
                          </p>
                          <ul className="space-y-1">
                            {metricToSourceMap[activeDigitalMetric].map(sourceIndex => (
                              <li key={sourceIndex} className="flex items-start">
                                <span className="text-blue-500 mr-1">•</span>
                                <span className="flex-1">
                                  {digitalImpactSources[sourceIndex].name} (
                                  {digitalImpactSources[sourceIndex].organization},{' '}
                                  {digitalImpactSources[sourceIndex].year})
                                  <a
                                    href={digitalImpactSources[sourceIndex].url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:text-blue-800 inline-flex items-center ml-1"
                                  >
                                    <ExternalLink className="h-2.5 w-2.5 ml-0.5" />
                                  </a>
                                </span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default DigitalImpactChart;
